import { useRouteContext } from "@tanstack/react-router";
import { Container } from "react-bootstrap";
import { UseFormReturn, useWatch } from "react-hook-form";

import DateInput from "@/components/shared/forms/DateInput";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import { type SLNG, SLNG_TEMPLATE } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import { FaBriefcase } from "react-icons/fa";
import { MdLanguage } from "react-icons/md";
import { LanguageOption, useSLNGFormLogic } from "./useSLNGFormLogic";
import { LanguageMultiSelect } from "@/components/shared/LanguageMultiSelect";

export function SLNGAddEditForm({
  selectedSLNG,
  form,
  isOpen,
}: {
  selectedSLNG: SLNG | undefined;
  form: UseFormReturn<SLNG, any, SLNG>;
  isOpen: boolean;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const viewOnly = !leaContext.hasRole("SLNGEdit");
  const coreData = useCoreData();
  const tooltipMap = leaContext.coreData.tooltipMap.get("SLNG");

  const {
    register,
    control,
    formState: { errors },
    getValues,
  } = form;

  const programYear = useWatch({ control, name: "ProgramYear" });

  const preschoolOptions = leaContext
    .currentOrgPreschoolsByProgramYear(programYear)
    .map((x) => ({
      Text: x.PreschoolName,
      Value: x.PreschoolCDSCode,
    }));

  const languageOptions: LanguageOption[] = (coreData.languagesList ?? []).map(
    (x: any) => ({
      Text: x.Text ?? x.text ?? "",
      Value: x.Value ?? x.value ?? "",
    })
  );

  const resetKey = selectedSLNG
    ? [
        selectedSLNG.CSPPID ?? "",
        selectedSLNG.EffectiveStartDate ?? "",
        selectedSLNG.ProgramYear ?? "",
        selectedSLNG.PreschoolCode ?? "",
        selectedSLNG.LocalChildId ?? "",
      ].join("|")
    : "new";

  const newSLNGTemplate: SLNG = {
    ...SLNG_TEMPLATE,
    ...(getValues() as any),
  };

  const slng = selectedSLNG ?? newSLNGTemplate;

  const {
    selectedHomeLanguages,
    onHomeLanguagesChange,
    validateEffectiveStartDate,
  } = useSLNGFormLogic({
    form,
    slng,
    resetKey,
    languageOptions,
    isOpen,
    isEdit: !!selectedSLNG,
    currentValues: getValues(),
  });

  return (
    <Container
      className="ps-3 pe-3 overview-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaBriefcase className="section-icon" />
          <h5 className="section-title">Agency Information</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="CSPPID"
              labelText="CSPPID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!(slng as any).CSPPID}
                  maxLength={50}
                  {...register(name)}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="EffectiveStartDate"
              labelText="Effective Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  max="9999-12-31"
                  disabled={viewOnly}
                  {...register(name, {
                    validate: validateEffectiveStartDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedSLNG?.ProgramYear && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Program Year is required" }}
                    options={coreData.programYearList}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, {
                      required: "Program Year is required",
                    })}
                    options={coreData.programYearList}
                  />
                )
              }
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedSLNG?.PreschoolCode && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Preschool is required" }}
                    options={preschoolOptions}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, { required: "Preschool is required" })}
                    options={preschoolOptions}
                  />
                )
              }
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="LocalChildId"
              labelText="Local Child ID (CICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!(slng as any).LocalChildId}
                  maxLength={50}
                  {...register(name, {
                    required: "Local Child ID (CICN) is required",
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <div className="form-card-modern mb-3">
        <div className="section-header">
          <MdLanguage className="section-icon" />
          <h5 className="section-title">Language Information</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="InterviewHomeLanguage1"
              labelText="Interview Home Language(s)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name, validationProps) => (
                <LanguageMultiSelect
                  id={id}
                  ariaLabel="Interview Home Language(s)"
                  ariaDescribedBy={validationProps["aria-describedby"]}
                  disabled={viewOnly}
                  required
                  invalid={!!validationProps["aria-invalid"]}
                  data={languageOptions}
                  value={selectedHomeLanguages}
                  onChange={onHomeLanguagesChange}
                  placeholder="Select up to 5"
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="InterviewMostUsedLanguage"
              labelText="Interview Most Used Language"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, {
                    required: "Interview most used language is required",
                  })}
                  options={coreData.languagesList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>
    </Container>
  );
}
